/**
 * User-facing error translation for MarketIQ.
 *
 * Every failure shown to an operator should say what happened and what to do
 * next. Machine reason codes stay available for the UI; technical detail
 * (stack traces, SQL errors, raw vendor payloads) is kept for developers and
 * never rendered in a toast or banner.
 */

export type FriendlyError = {
  message: string;
  nextAction?: string;
  reason?: string;
  reference?: string;
  /** Raw technical text, only present so callers can log it in dev. */
  technical?: string;
};

const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  linkedin_company: "LinkedIn company page",
  linkedin_personal: "LinkedIn profile",
  twitter: "X (Twitter)",
  threads: "Threads",
  meta_ads: "Meta",
  google_analytics: "Google Analytics",
  google_search_console: "Google Search Console",
  ai: "the AI service",
  website_crawler: "the website crawler",
  pagespeed_insights: "Google PageSpeed",
  google_trends: "Google Trends",
  competitor_intelligence: "Competitor Intelligence",
  backlinks: "the backlink publisher",
};

const REASONS: Record<string, [string, string]> = {
  not_connected: [
    "{platform} is not connected yet.",
    "Open Integrations, connect {platform}, then try again.",
  ],
  credentials_missing: [
    "The saved {platform} credentials are incomplete.",
    "Reconnect {platform} in Integrations to refresh them.",
  ],
  token_expired: [
    "The {platform} connection has expired.",
    "Reconnect {platform} in Integrations to keep this working.",
  ],
  missing_page_id: [
    "No Facebook Page is selected for this workspace.",
    "Add the Page ID in Integrations, then sync again.",
  ],
  missing_ig_user_id: [
    "No Instagram business account is selected for this workspace.",
    "Add the Instagram user ID in Integrations, then sync again.",
  ],
  missing_organization_urn: [
    "The LinkedIn company page is not linked to this workspace.",
    "Add the company page ID in Integrations, then sync again.",
  ],
  missing_member_urn: [
    "The LinkedIn profile is not linked to this workspace.",
    "Reconnect LinkedIn in Integrations, then sync again.",
  ],
  missing_threads_user_id: [
    "The Threads account is not linked to this workspace.",
    "Reconnect Threads in Integrations, then sync again.",
  ],
  threads_caption_too_long: [
    "Your post is over 500 characters, please shorten it.",
    "Trim the caption, then publish again — Threads does not truncate it for you.",
  ],
  threads_image_unsupported: [
    "Unsupported image format or size. Please use JPEG or PNG format under 8MB.",
    "Re-export the image as JPEG or PNG under 8MB, then publish again.",
  ],
  threads_video_unsupported: [
    "Unsupported video format, file size over 500MB, or duration over 5 minutes. Please use MP4 or MOV formats within limits.",
    "Re-export the video as MP4 or MOV under 500MB and 5 minutes, then publish again.",
  ],
  threads_reply_too_long: [
    "Threads allows 500 characters per reply, and this reply is longer.",
    "Shorten the reply, then send it again.",
  ],
  threads_publish_limit_reached: [
    "Threads has used its daily API publishing quota (250 posts per 24 hours).",
    "Wait for the quota window to reset, or publish fewer posts from MarketIQ.",
  ],
  threads_reply_limit_reached: [
    "Threads has used its daily API reply quota (1,000 replies per 24 hours).",
    "Wait for the quota window to reset before sending more replies.",
  ],
  threads_container_failed: [
    "Threads could not process the attached video.",
    "Re-export it as MP4/MOV (H.264, 5 minutes or shorter, under 1 GB), then try again.",
  ],
  threads_container_not_ready: [
    "Threads is still preparing this reply.",
    "Wait a few seconds, then send it again — nothing was posted yet.",
  ],
  threads_container_timeout: [
    "Threads is still processing the attached video.",
    "Wait a minute, then publish again — nothing was posted yet.",
  ],
  threads_insights_unsupported: [
    "Threads insights are not available for this connection.",
    "Metrics would need the threads_manage_insights permission, which MarketIQ does not request yet.",
  ],
  token_invalid: [
    "The {platform} connection is no longer valid.",
    "Reconnect {platform} in Integrations, then try again.",
  ],
  api_forbidden: [
    "{platform} refused the request because the connection is missing a permission.",
    "Reconnect {platform} in Integrations and approve every requested permission.",
  ],
  missing_scope: [
    "The connected {platform} account is missing a permission this action needs.",
    "Reconnect {platform} in Integrations and approve all requested permissions.",
  ],
  // Instagram Login (Business Login for Instagram) uses its own app id/secret; Meta
  // answers "Invalid platform app" when the Facebook app id is used by mistake. The
  // Integrations toast renders the message, so the env var + dashboard path live there.
  instagram_app_id_invalid: [
    "Meta rejected the Instagram app ID. Set MARKETING_INSTAGRAM_APP_ID to the Instagram app ID from App Dashboard → Instagram → API setup with Instagram login → 3. Set up Instagram business login → Business login settings (not the Facebook app id), then connect again.",
    "An admin updates MARKETING_INSTAGRAM_APP_ID on the server and restarts the marketing service.",
  ],
  instagram_app_secret_invalid: [
    "Meta rejected the Instagram app secret. Set MARKETING_INSTAGRAM_APP_SECRET to the Instagram app secret from App Dashboard → Instagram → API setup with Instagram login → 3. Set up Instagram business login → Business login settings, then connect again.",
    "An admin updates MARKETING_INSTAGRAM_APP_SECRET on the server and restarts the marketing service.",
  ],
  // Instagram authorization codes are single-use: a repeated callback (double-load, refresh,
  // back/forward) fails here even when the first attempt already succeeded.
  instagram_code_invalid: [
    "Instagram already used that authorization (its codes can only be used once). Click Connect once to start a fresh sign-in.",
    "Nothing was saved by the failed attempt — just reconnect once.",
  ],
  // Meta's generic "Error validating verification code…" wording: the callback URI matches,
  // so the remaining difference in the exchange tuple is the app credentials.
  instagram_exchange_rejected: [
    "Meta rejected the token exchange. Set MARKETING_INSTAGRAM_APP_SECRET to the Instagram app secret from App Dashboard → Instagram → API setup with Instagram login → 3. Set up Instagram business login → Business login settings, then click Connect once.",
    "An admin sets the Instagram app secret on the server (it currently falls back to the Facebook app secret) and restarts the marketing service.",
  ],
  instagram_redirect_uri_mismatch: [
    "Meta rejected the callback address. In App Dashboard → Instagram → API setup with Instagram login → 3. Set up Instagram business login → Business login settings, make the OAuth redirect URI match our callback exactly, then click Connect once more.",
    "An admin aligns the registered redirect URI, then the connection can be retried.",
  ],
  instagram_permission_missing: [
    "The Instagram app is missing a required permission. Enable instagram_business_basic, instagram_business_content_publish and instagram_business_manage_comments for the Instagram app, then connect again.",
    "An admin enables those permissions in Business login settings.",
  ],
  instagram_token_exchange_failed: [
    "Instagram refused to complete the connection. The exact reason is in the marketing service log, and clicking Connect once more is safe.",
    "If it repeats, verify the Instagram app ID and app secret shown in Business login settings.",
  ],
  permission_denied: [
    "You do not have permission to do this.",
    "Ask a workspace admin to grant you access, or sign in with an admin account.",
  ],
  rate_limited: [
    "{platform} is temporarily limiting how many requests we can send.",
    "Wait a few minutes, then try again.",
  ],
  quota_exceeded: [
    "You have reached the limit for this action on your current plan.",
    "Try again in the next cycle, or upgrade your plan.",
  ],
  vendor_unavailable: [
    "{platform} is having problems right now.",
    "Nothing to fix on your side — try again in a few minutes.",
  ],
  network_error: [
    "MarketIQ could not reach {platform}.",
    "Check the connection, then try again.",
  ],
  timeout: [
    "This is taking longer than usual.",
    "Try again — long syncs (SEO, PageSpeed, AI) can take a few minutes.",
  ],
  approval_required: [
    "This reply still needs approval before it can be sent.",
    "Approve the draft, then publish it.",
  ],
  already_sent: ["This reply was already published.", "Refresh the conversation to see it."],
  empty_reply: ["The reply is empty.", "Write a short reply before sending."],
  missing_parent_id: [
    "There is nothing to reply to in this conversation yet.",
    "Sync the channel so the original comment or post is loaded, then try again.",
  ],
  not_found: ["That item is no longer available.", "Refresh the page and pick it again."],
  conflict: [
    "Someone changed this item while you were editing it.",
    "Refresh to load the latest version, then apply your change again.",
  ],
  validation_error: [
    "Some details need your attention.",
    "Review the highlighted fields and save again.",
  ],
  platform_not_supported: [
    "This channel is not supported for this action yet.",
    "Use one of the connected channels shown in Integrations.",
  ],
  db_unavailable: [
    "MarketIQ could not save your change because its database was unreachable.",
    "Try again in a moment — if it persists, contact support with the reference code.",
  ],
  ai_unavailable: [
    "The AI assistant is unavailable right now.",
    "Try again shortly, or write the text yourself.",
  ],
  internal_error: [
    "Something went wrong on our side.",
    "Try again. If it keeps happening, contact support with the reference code.",
  ],
  publish_failed: [
    "The post could not be published.",
    "Check the channel connection in Integrations, then try again.",
  ],
  api_error: ["{platform} rejected the request.", "Check the details you entered, then try again."],
  oauth_failed: [
    "The sign-in with {platform} could not be completed.",
    "Start the connection again from Integrations.",
  ],
  invalid_token: [
    "The {platform} connection is no longer valid.",
    "Reconnect {platform} in Integrations.",
  ],
};

const STATUS_MESSAGES: Record<number, [string, string]> = {
  400: ["That request could not be completed.", "Check the details you entered, then try again."],
  401: ["Your session expired.", "Sign in again to continue."],
  403: [
    "You do not have access to this.",
    "Ask a workspace admin for access, or reconnect the channel in Integrations.",
  ],
  404: ["That item is no longer available.", "Refresh the page and try again."],
  409: [
    "This item changed while you were working on it.",
    "Refresh to load the latest version, then try again.",
  ],
  413: ["That file is too large.", "Upload a smaller file and try again."],
  422: ["Some details need your attention.", "Review the highlighted fields and save again."],
  429: ["Too many requests in a short time.", "Wait a few minutes, then try again."],
};

const TECHNICAL_MARKERS = [
  "traceback",
  "psycopg",
  "sqlalchemy",
  "sqlite3",
  "operationalerror",
  "integrityerror",
  "connection refused",
  "connection reset",
  'file "',
  "at 0x",
  "httpx.",
  "redis",
  "keyerror",
  "typeerror",
  "attributeerror",
  "valueerror:",
  "exception(",
  "caused by",
  "stack trace",
  '{"',
  'error":',
];

export type ErrorInput = {
  status?: number;
  code?: string;
  reason?: string;
  message?: string;
  nextAction?: string;
  reference?: string;
  detail?: unknown;
  technical?: string;
  platform?: string;
};

function platformLabel(platform?: string): string {
  const key = String(platform || "").trim().toLowerCase();
  if (!key) return "this channel";
  if (PLATFORM_LABELS[key]) return PLATFORM_LABELS[key];
  const hit = Object.keys(PLATFORM_LABELS).find((prefix) => key.startsWith(prefix));
  return hit ? PLATFORM_LABELS[hit] : key.replace(/_/g, " ");
}

/** True when text looks like a stack trace, SQL error or raw vendor payload. */
export function looksTechnical(text: unknown): boolean {
  const value = String(text ?? "").trim();
  if (!value) return false;
  if (value.includes("\n") || value.includes("\r")) return true;
  const lowered = value.toLowerCase();
  return TECHNICAL_MARKERS.some((marker) => lowered.includes(marker));
}

function template(pair: [string, string], platform?: string): [string, string] {
  const label = platformLabel(platform);
  return [pair[0].replace("{platform}", label), pair[1].replace("{platform}", label)];
}

/**
 * Make a plain error string safe to render.
 * Known reason codes become their friendly sentence; raw technical text is
 * replaced and logged for developers instead of being shown.
 */
export function friendlyErrorText(value: unknown, fallback = "Something went wrong"): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (Object.prototype.hasOwnProperty.call(REASONS, text)) {
    return describeError({ reason: text }).message;
  }
  if (looksTechnical(text)) {
    const isDev = typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);
    if (isDev) {
      // eslint-disable-next-line no-console
      console.debug("[MarketIQ:ui] technical detail (not shown to users):", text);
    }
    return fallback;
  }
  return text;
}

/**
 * Turn a backend error into something an operator can read and act on.
 * Unknown reasons fall back to status-code guidance, then to a safe default.
 */
export function describeError(input: ErrorInput, fallback = "Something went wrong"): FriendlyError {
  const reason = String(input.reason || "").trim();
  const detailText = typeof input.detail === "string" ? input.detail : "";
  const serverMessage = String(input.message || "").trim();
  const status = Number(input.status || 0);
  const platform = (input as ErrorInput & { platform?: string }).platform;

  const result: FriendlyError = { message: fallback };
  if (reason) result.reason = reason;
  if (input.reference) result.reference = input.reference;
  if (input.technical) result.technical = input.technical;

  if (reason && REASONS[reason]) {
    const [message, nextAction] = template(REASONS[reason], platform);
    result.message = message;
    result.nextAction = input.nextAction || nextAction;
    return result;
  }

  // A friendly server message always wins over a generic status message.
  const candidate = serverMessage || detailText;
  if (candidate && !looksTechnical(candidate)) {
    result.message = candidate;
    if (input.nextAction) result.nextAction = input.nextAction;
    return result;
  }
  if (candidate) result.technical = input.technical || candidate;

  if (status && STATUS_MESSAGES[status]) {
    const [message, nextAction] = template(STATUS_MESSAGES[status], platform);
    result.message = message;
    result.nextAction = input.nextAction || nextAction;
    return result;
  }
  if (status >= 500) {
    const [message, nextAction] = template(REASONS.internal_error, platform);
    result.message = message;
    result.nextAction = input.nextAction || nextAction;
    return result;
  }
  if (input.nextAction) result.nextAction = input.nextAction;
  return result;
}

